import { useState, useRef } from "react";

interface FieldConfig {
  name: string;
  label: string;
  type: string;
  icon: string;
  required: boolean;
  placeholder: string;
}

interface ContactFormProProps {
  variant?: "split" | "contact-page" | "compact" | "inline";
  title?: string;
  subtitle?: string;
  formId?: string;
  ctaTitle?: string;
  ctaSubtitle?: string;
  pageName?: string;
  useVille?: boolean;
  className?: string;
}

export default function ContactFormPro({
  variant = "split",
  title,
  subtitle,
  formId = "devis-form",
  ctaTitle,
  ctaSubtitle,
  pageName,
  useVille = false,
  className = "",
}: ContactFormProProps) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({
    nom: "",
    telephone: "",
    email: "",
    codepostal: "",
    ville: "",
    message: "",
  });
  const [hoveredInfo, setHoveredInfo] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const data = new FormData(e.target as HTMLFormElement);
      const res = await fetch("https://formspree.io/f/mdajjjpn", {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setStatus("sent");
        setFormData({ nom: "", telephone: "", email: "", codepostal: "", ville: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const defaultFields: FieldConfig[] = [
    { name: "nom", label: "Nom", type: "text", icon: "ri-user-line", required: true, placeholder: "Votre nom" },
    { name: "telephone", label: "Téléphone", type: "tel", icon: "ri-phone-line", required: true, placeholder: "Votre téléphone" },
    { name: "email", label: "E-mail", type: "email", icon: "ri-mail-line", required: false, placeholder: "Votre e-mail" },
    useVille
      ? { name: "ville", label: "Ville", type: "text", icon: "ri-map-pin-line", required: false, placeholder: "Votre ville" }
      : { name: "codepostal", label: "Code postal", type: "text", icon: "ri-map-pin-line", required: false, placeholder: "Votre code postal" },
  ];

  const displayTitle = title || (
    variant === "inline" ? "Demandez un devis gratuit" : "Demandez un devis gratuit"
  );
  const displaySubtitle = subtitle || (
    variant === "inline"
      ? "Réponse sous 2h. Devis sans engagement."
      : "Réponse sous 2h. Devis sans engagement."
  );

  const isInline = variant === "inline";
  const isContactPage = variant === "contact-page";
  const isCompact = variant === "compact";
  const isSplit = variant === "split";

  const infoCards = [
    {
      icon: "ri-phone-line",
      title: "Téléphone",
      value: "03 74 47 10 36",
      href: "tel:0374471036",
      sub: "Appel gratuit",
    },
    {
      icon: "ri-map-pin-line",
      title: "Atelier DF PRO",
      value: "82 rue de Heidenberg",
      sub: "67200 Strasbourg",
    },
    {
      icon: "ri-time-line",
      title: "Horaires",
      value: "Lun – Sam : 8h – 18h",
      sub: "Urgences 7j/7",
    },
    {
      icon: "ri-mail-line",
      title: "E-mail",
      value: "info@reparation-fenetre-strasbourg.fr",
      href: "mailto:info@reparation-fenetre-strasbourg.fr",
      sub: "Réponse sous 2h",
    },
  ];

  const whyCards = [
    "Devis gratuit et sans engagement",
    "Déplacement gratuit dans tout le Bas-Rhin",
    "Pièce comprise dans l'intervention",
    "Intervention en 24–48 h, urgences le jour même",
  ];

  return (
    <section id="devis" className={`relative overflow-hidden ${isContactPage ? "bg-[#EEF2F4]" : "bg-[#1E3A47]"} py-10 md:py-14 lg:py-16 ${className}`}>
      {/* Animated background for non-contact-page variants */}
      {!isContactPage && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A47] via-[#2A5F5A] to-[#FF6B4A]/20" />
          <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-[#FF6B4A]/8 rounded-full blur-[120px] animate-float-slow" />
          <div className="absolute -bottom-20 -right-20 w-[600px] h-[600px] bg-[#1E3A47]/30 rounded-full blur-[120px] animate-float" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#FF6B4A]/5 rounded-full blur-[100px] animate-pulse-soft" />
        </>
      )}

      <div className={`relative w-full px-4 md:px-8 lg:px-12 max-w-[1280px] mx-auto ${isContactPage ? "" : "z-10"}`}>
        {/* Inline CTA */}
        {isInline && ctaTitle && (
          <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-6 md:p-10 mb-8 md:mb-10 text-center animate-fade-in-up">
            <h2 className="font-serif text-xl md:text-2xl font-bold text-white mb-3">
              {ctaTitle}
            </h2>
            <p className="text-white/70 text-base mb-6 max-w-2xl mx-auto">
              {ctaSubtitle}
            </p>
            <a
              href="tel:0374471036"
              className="inline-flex items-center justify-center gap-2 bg-[#FF6B4A] text-white px-8 py-4 rounded-xl font-semibold text-base whitespace-nowrap hover:bg-[#FF6B4A]/90 transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-[#FF6B4A]/30 animate-breathe"
            >
              <i className="ri-phone-line w-5 h-5 flex items-center justify-center" />
              Appeler le 03 74 47 10 36
            </a>
          </div>
        )}

        <div className={`flex flex-col ${isCompact || isContactPage ? "lg:flex-row" : "lg:flex-row"} gap-10 lg:gap-14`}>
          {/* LEFT SIDE */}
          <div className={`${isCompact || isContactPage ? "w-full lg:w-[45%]" : "w-full lg:w-[35%]"}`}>
            {/* Header */}
            <div className="mb-8">
              <p className={`font-serif italic text-sm md:text-base mb-4 ${isContactPage ? "text-[#1E3A47]" : "text-white/60"}`}>
                Contactez DF PRO
              </p>
              <a
                href="tel:0374471036"
                className={`block font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-6 hover:underline transition-all ${isContactPage ? "text-[#FF6B4A]" : "text-white"} animate-breathe`}
              >
                03 74 47 10 36
              </a>
              <p className={`text-sm md:text-base ${isContactPage ? "text-[#243038]/70" : "text-white/70"}`}>
                {displaySubtitle}
              </p>
            </div>

            {/* Info cards */}
            <div className="space-y-3">
              {infoCards.map((card, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHoveredInfo(i)}
                  onMouseLeave={() => setHoveredInfo(null)}
                  className={`flex items-start gap-4 p-4 rounded-2xl border transition-all duration-300 cursor-default ${
                    isContactPage
                      ? hoveredInfo === i
                        ? "bg-white border-[#FF6B4A]/30 shadow-lg shadow-[#FF6B4A]/10"
                        : "bg-white border-[#D7E0E4]"
                      : hoveredInfo === i
                        ? "bg-white/15 border-white/30 shadow-lg shadow-[#FF6B4A]/10 backdrop-blur-sm"
                        : "bg-white/10 border-white/10 backdrop-blur-sm"
                  }`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                    hoveredInfo === i
                      ? "bg-[#FF6B4A] text-white shadow-md shadow-[#FF6B4A]/30"
                      : isContactPage
                        ? "bg-[#EEF2F4] text-[#1E3A47]"
                        : "bg-white/15 text-white/80"
                  }`}>
                    <i className={`${card.icon} w-5 h-5 flex items-center justify-center`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium uppercase tracking-wide mb-0.5 ${isContactPage ? "text-[#243038]/50" : "text-white/50"}`}>
                      {card.title}
                    </p>
                    {card.href ? (
                      <a href={card.href} className={`text-sm md:text-base font-semibold hover:underline transition-colors ${isContactPage ? "text-[#243038] hover:text-[#FF6B4A]" : "text-white hover:text-[#FF6B4A]"}`}>
                        {card.value}
                      </a>
                    ) : (
                      <p className={`text-sm md:text-base font-semibold ${isContactPage ? "text-[#243038]" : "text-white"}`}>
                        {card.value}
                      </p>
                    )}
                    <p className={`text-xs ${isContactPage ? "text-[#243038]/60" : "text-white/60"}`}>
                      {card.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE — FORM */}
          <div className={`${isCompact || isContactPage ? "w-full lg:w-[55%]" : "w-full lg:w-[65%]"}`}>
            <div className={`rounded-3xl border p-6 md:p-8 lg:p-10 transition-all duration-500 ${
              isContactPage
                ? "bg-white border-[#D7E0E4] shadow-lg shadow-[#1E3A47]/5"
                : "bg-white/95 backdrop-blur-xl border-white/30 shadow-2xl shadow-black/10"
            }`}>
              {/* Form header */}
              <div className="mb-8">
                <h2 className="text-xl md:text-2xl font-serif font-bold text-[#1E3A47] mb-2">
                  {displayTitle}
                </h2>
                <p className="text-[#243038]/70 text-sm md:text-base">
                  {displaySubtitle}
                </p>
              </div>

              {/* Form */}
              <form
                ref={formRef}
                data-readdy-form
                id={formId}
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {defaultFields.map((field, idx) => (
                    <div
                      key={field.name}
                      className="relative group animate-fade-in-up"
                      style={{ animationDelay: `${idx * 80}ms` }}
                    >
                      <label
                        htmlFor={`${formId}-${field.name}`}
                        className={`absolute left-12 transition-all duration-300 ease-out pointer-events-none z-10 ${
                          focusedField === field.name || formData[field.name]
                            ? "top-1 text-[10px] text-[#FF6B4A] font-semibold"
                            : "top-3.5 text-sm text-[#243038]/40"
                        }`}
                      >
                        {field.label}
                        {field.required && <span className="text-[#FF6B4A] ml-0.5">*</span>}
                      </label>
                      <div className={`absolute left-4 top-3.5 transition-all duration-300 z-10 ${
                        focusedField === field.name
                          ? "text-[#FF6B4A] scale-110"
                          : "text-[#243038]/25"
                      }`}>
                        <i className={`${field.icon} w-5 h-5 flex items-center justify-center`} />
                      </div>
                      <input
                        type={field.type}
                        id={`${formId}-${field.name}`}
                        name={field.name}
                        required={field.required}
                        value={formData[field.name]}
                        onChange={handleChange}
                        onFocus={() => setFocusedField(field.name)}
                        onBlur={() => setFocusedField(null)}
                        className="w-full h-[58px] pl-12 pr-4 pt-5 pb-2 rounded-2xl border-2 border-[#D7E0E4] bg-[#EEF2F4]/50 text-sm text-[#243038] focus:outline-none focus:border-[#FF6B4A] focus:bg-white focus:shadow-[0_0_0_4px_rgba(255,107,74,0.12)] transition-all duration-300 placeholder:text-[#243038]/20"
                        placeholder={focusedField === field.name ? field.placeholder : ""}
                      />
                    </div>
                  ))}
                </div>

                {/* Textarea */}
                <div
                  className="relative group animate-fade-in-up"
                  style={{ animationDelay: `${defaultFields.length * 80}ms` }}
                >
                  <label
                    htmlFor={`${formId}-message`}
                    className={`absolute left-4 transition-all duration-300 ease-out pointer-events-none z-10 ${
                      focusedField === "message" || formData.message
                        ? "top-1 text-[10px] text-[#FF6B4A] font-semibold"
                        : "top-3.5 text-sm text-[#243038]/40"
                    }`}
                  >
                    Message
                  </label>
                  <textarea
                    id={`${formId}-message`}
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    className="w-full px-4 pt-6 pb-3 rounded-2xl border-2 border-[#D7E0E4] bg-[#EEF2F4]/50 text-sm text-[#243038] focus:outline-none focus:border-[#FF6B4A] focus:bg-white focus:shadow-[0_0_0_4px_rgba(255,107,74,0.12)] transition-all duration-300 resize-none placeholder:text-[#243038]/20"
                    placeholder={focusedField === "message" ? "Décrivez votre problème de fenêtre..." : ""}
                  />
                  <p className="text-xs text-[#243038]/40 mt-1.5 text-right">
                    <span className={formData.message.length > 450 ? "text-[#FF6B4A] font-semibold" : ""}>
                      {formData.message.length}
                    </span>
                    /500
                  </p>
                </div>

                {/* Honeypot */}
                <div className="absolute opacity-0 w-0 h-0 overflow-hidden">
                  <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" />
                </div>

                {/* Submit + messages */}
                <div className="flex flex-col items-start gap-4 animate-fade-in-up" style={{ animationDelay: `${(defaultFields.length + 1) * 80}ms` }}>
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="relative inline-flex items-center justify-center gap-2 w-full sm:w-auto overflow-hidden bg-gradient-to-r from-[#FF6B4A] to-[#FF8F6A] text-white px-10 py-4 rounded-2xl font-semibold text-base whitespace-nowrap transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-[#FF6B4A]/25 active:scale-[0.97] disabled:opacity-70 disabled:scale-100 disabled:cursor-not-allowed group"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    {status === "sending" ? (
                      <>
                        <span className="w-5 h-5 border-[3px] border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="relative z-10">Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <span className="relative z-10">Envoyer ma demande</span>
                        <i className="ri-send-plane-fill relative z-10 w-5 h-5 flex items-center justify-center" />
                      </>
                    )}
                  </button>

                  {status === "sent" && (
                    <div className="w-full sm:w-auto flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-4 rounded-2xl text-sm font-medium animate-success-pop">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-emerald-600 w-5 h-5 flex items-center justify-center" />
                      </div>
                      <div>
                        <p className="font-semibold">Demande envoyée !</p>
                        <p className="text-emerald-600/80">Merci, votre demande a bien été envoyée. Nous vous rappelons rapidement au 03 74 47 10 36.</p>
                      </div>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="w-full sm:w-auto flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-2xl text-sm font-medium animate-success-pop">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-600 w-5 h-5 flex items-center justify-center" />
                      </div>
                      <div>
                        <p className="font-semibold">Une erreur est survenue</p>
                        <p className="text-red-600/80">Appelez-nous directement au 03 74 47 10 36.</p>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Contact page map */}
            {isContactPage && (
              <div className="mt-6 rounded-3xl border border-[#D7E0E4] overflow-hidden shadow-lg shadow-[#1E3A47]/5">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2637.0!2d7.7347!3d48.5734!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4796c84d5e1e7e6b%3A0x8f5f3c3c3c3c3c3c!2s82%20Rue%20de%20Heidenberg%2C%2067200%20Strasbourg!5e0!3m2!1sfr!2sfr!4v1"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation DF PRO Strasbourg"
                  aria-label="Carte Google montrant l'atelier DF PRO au 82 rue de Heidenberg, Strasbourg"
                />
              </div>
            )}

            {/* Compact why cards */}
            {isCompact && (
              <div className="mt-6 bg-[#1E3A47] rounded-3xl p-6 md:p-8 text-white">
                <h3 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center" />
                  Pourquoi nous contacter ?
                </h3>
                <ul className="space-y-3">
                  {whyCards.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm md:text-base">
                      <div className="w-6 h-6 rounded-full bg-[#FF6B4A]/20 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#FF6B4A] w-4 h-4 flex items-center justify-center text-xs" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Inline bottom CTA */}
        {isInline && (
          <div className="mt-12 md:mt-16 text-center">
            <p className="text-white/70 text-base mb-4">
              Préférez nous appeler directement ?
            </p>
            <a
              href="tel:0374471036"
              className="inline-flex items-center justify-center gap-2 bg-[#FF6B4A] text-white px-8 py-4 rounded-xl font-semibold text-base whitespace-nowrap hover:bg-[#FF6B4A]/90 transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-[#FF6B4A]/30"
            >
              <i className="ri-phone-line w-5 h-5 flex items-center justify-center" />
              Appeler le 03 74 47 10 36
            </a>
          </div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-20px) translateX(10px); }
          66% { transform: translateY(10px) translateX(-10px); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(-15px); }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes success-pop {
          0% { opacity: 0; transform: scale(0.92) translateY(10px); }
          60% { transform: scale(1.02) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 12s ease-in-out infinite;
        }
        .animate-pulse-soft {
          animation: pulse-soft 6s ease-in-out infinite;
        }
        .animate-breathe {
          animation: breathe 3s ease-in-out infinite;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out forwards;
          opacity: 0;
        }
        .animate-success-pop {
          animation: success-pop 0.5s ease-out forwards;
        }
      `}</style>
    </section>
  );
}